import React, { useLayoutEffect, useMemo, useRef } from 'react';
import ServiceAccordionItem from './ServiceAccordionItem';
import AccordionConnector from './AccordionConnector';
import { getServiceRouteKey } from '../utils/serviceContent';
import { useLocale } from '../context/LocaleContext';
import './ServiceAccordion.css';

const ServiceAccordion = ({ services, activeService, onServiceClick }) => {
  const { localePath } = useLocale();
  const accordionRef = useRef(null);
  const accordionRefs = useRef({});
  const connectorRef = useRef(null);
  const connectorStateRef = useRef({
    hasPosition: false,
    orderKey: '',
  });
  const serviceOrderKey = useMemo(
    () => services.map((service) => getServiceRouteKey(service)).join('|'),
    [services]
  );

  useLayoutEffect(() => {
    const updateConnectorPosition = () => {
      const accordionContainer = accordionRef.current;
      const activeItem = accordionRefs.current[activeService];
      const connector = connectorRef.current;

      if (!accordionContainer || !activeItem || !connector) {
        if (connector) connector.dataset.ready = 'false';
        return;
      }

      const containerRect = accordionContainer.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();
      const center = itemRect.left - containerRect.left + itemRect.width / 2;
      const canAnimate = connectorStateRef.current.hasPosition
        && connectorStateRef.current.orderKey === serviceOrderKey;

      connector.dataset.animate = canAnimate ? 'true' : 'false';
      connector.style.left = `${center}px`;
      connector.dataset.ready = 'true';

      connectorStateRef.current = {
        hasPosition: true,
        orderKey: serviceOrderKey,
      };

      if (!canAnimate) {
        window.requestAnimationFrame(() => {
          if (connectorRef.current === connector) {
            connector.dataset.animate = 'true';
          }
        });
      }
    };

    updateConnectorPosition();

    const accordionContainer = accordionRef.current;
    const activeItem = accordionRefs.current[activeService];
    const resizeObserver = typeof ResizeObserver !== 'undefined' && accordionContainer
      ? new ResizeObserver(updateConnectorPosition)
      : null;
    resizeObserver?.observe(accordionContainer);
    if (activeItem) resizeObserver?.observe(activeItem);

    window.addEventListener('resize', updateConnectorPosition);

    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) updateConnectorPosition();
    });

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateConnectorPosition);
    };
  }, [activeService, serviceOrderKey]);

  const setItemRef = (serviceKey, element) => {
    if (element) {
      accordionRefs.current[serviceKey] = element;
      return;
    }
    delete accordionRefs.current[serviceKey];
  };

  return (
    <div className="services-accordion" ref={accordionRef}>
      {services.map((service) => {
        const serviceKey = getServiceRouteKey(service);
        return (
          <ServiceAccordionItem
            key={service._id || serviceKey}
            service={service}
            isActive={String(activeService) === serviceKey}
            to={localePath(`/services/${service.slug}`)}
            onClick={() => onServiceClick?.(serviceKey)}
            itemRef={(element) => setItemRef(serviceKey, element)}
          />
        );
      })}
      <AccordionConnector connectorRef={connectorRef} />
    </div>
  );
};

export default ServiceAccordion;

